import { useT, Namespace } from "@racoon/i18n";
import { Button, useToast } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import {
  useGetOrganizationSettingsQuery,
  useUpdateDisabledJobTypesMutation,
} from "~/shared/api/organization/organization.grpc";
import type { JobTypeOption } from "~/shared/hooks/useJobTypeOptions";
import { JobTypeStatusPill } from "./job-type-status-pill";

interface JobTypeFormProps {
  option: JobTypeOption;
  onClose: () => void;
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm font-medium leading-4 tracking-[-0.2px] text-text-body">{label}</span>
    {children}
  </div>
);

/** Edit modal body: «Отключить» / «Активировать» saves at once. */
export const JobTypeForm = ({ option, onClose }: JobTypeFormProps) => {
  const t = useT();
  const { toast } = useToast();
  // Read live, not passed in: the modal body is fixed at open time, and the whole list is
  // replaced on save, so a stale copy would undo an edit saved a moment earlier.
  const { data: settings, isFetching } = useGetOrganizationSettingsQuery({});
  const disabledTypes: ConnectProto.JobType[] = settings?.disabledJobTypes?.types ?? [];
  const active = !disabledTypes.includes(option.type);
  const [updateDisabledJobTypes, { isLoading: isSaving }] = useUpdateDisabledJobTypesMutation();
  const isLoading = isSaving || isFetching;

  const handleToggle = async () => {
    const others = disabledTypes.filter((type) => type !== option.type);
    try {
      await updateDisabledJobTypes({ types: active ? [...others, option.type] : others }).unwrap();
      toast({ title: t(Namespace.Settings, "jobTypes.dialog.saveSuccess") });
      onClose();
    } catch {
      // GlobalErrorToaster shows the API error.
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-4 p-4">
        <Field label={t(Namespace.Settings, "jobTypes.dialog.name")}>
          <div className="flex h-8 w-[220px] items-center rounded-lg border border-slate-200 p-2 text-sm leading-4 text-text-body">
            {option.label}
          </div>
        </Field>
        <Field label={t(Namespace.Settings, "jobTypes.dialog.endpoint")}>
          <div className="flex h-8 w-[220px] items-center rounded-lg bg-slate-200 p-2 text-sm leading-4 text-slate-400">
            {option.tariffKey}
          </div>
        </Field>
        <Field label={t(Namespace.Settings, "jobTypes.dialog.activity")}>
          <JobTypeStatusPill active={active} />
        </Field>
      </div>
      <div className="flex border-t border-slate-200 p-4">
        <Button
          id="job-type-toggle-button"
          size="sm"
          className="text-sm"
          variant={active ? "solidRed" : "solidGreen"}
          onClick={handleToggle}
          disabled={isLoading}
        >
          {t(Namespace.Settings, active ? "jobTypes.dialog.disable" : "jobTypes.dialog.activate")}
        </Button>
      </div>
    </div>
  );
};
